import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import PageTransition from "@/components/layout/PageTransition";
import PageHero from "@/components/common/PageHero";
import AnimatedSection from "@/components/common/AnimatedSection";
import { api } from "@/lib/api";

export default function CaseStudyDetailPage() {
  const { caseId } = useParams();
  const [caseStudy, setCaseStudy] = useState(null);

  useEffect(() => {
    const loadCaseStudy = async () => {
      try {
        const response = await api.get(`/public/case-studies/${caseId}`);
        setCaseStudy(response.data);
      } catch {
        toast.error("Unable to load this case study");
      }
    };

    loadCaseStudy();
  }, [caseId]);

  if (!caseStudy) {
    return (
      <PageTransition testId="case-study-detail-page-loading">
        <main className="mx-auto max-w-4xl px-6 py-20 md:px-10" data-testid="case-study-detail-loading-state">
          <p className="font-[Manrope] text-sm text-[#666666]" data-testid="case-study-detail-loading-text">
            Loading case study...
          </p>
        </main>
      </PageTransition>
    );
  }

  return (
    <PageTransition testId="case-study-detail-page">
      <PageHero
        title={caseStudy.title}
        subtitle={caseStudy.category}
        image={caseStudy.after_image}
        testId="case-study-detail-page-hero"
      />

      <main className="mx-auto max-w-6xl px-6 py-14 md:px-10 md:py-20" data-testid="case-study-detail-main-content">
        <AnimatedSection testId="case-study-detail-images-section">
          <div className="grid gap-5 md:grid-cols-2">
            <article className="overflow-hidden rounded-2xl border border-[#f2e6c9] bg-white" data-testid="case-study-detail-before-card">
              <img
                src={caseStudy.before_image}
                alt={`${caseStudy.title} before`}
                className="aspect-[5/4] w-full object-cover"
                data-testid="case-study-detail-before-image"
              />
              <p className="border-t border-[#f2e6c9] px-4 py-3 font-[Manrope] text-sm text-[#555555]" data-testid="case-study-detail-before-label">
                Before
              </p>
            </article>

            <article className="overflow-hidden rounded-2xl border border-[#f2e6c9] bg-white" data-testid="case-study-detail-after-card">
              <img
                src={caseStudy.after_image}
                alt={`${caseStudy.title} after`}
                className="aspect-[5/4] w-full object-cover"
                data-testid="case-study-detail-after-image"
              />
              <p className="border-t border-[#f2e6c9] px-4 py-3 font-[Manrope] text-sm text-[#555555]" data-testid="case-study-detail-after-label">
                After
              </p>
            </article>
          </div>
        </AnimatedSection>

        <AnimatedSection className="mt-8" testId="case-study-detail-text-section">
          <article className="rounded-2xl border border-[#f2e6c9] bg-white p-7 md:p-10" data-testid="case-study-detail-text-card">
            <p className="font-[Manrope] text-xs uppercase tracking-[0.16em] text-[#8b7a55]" data-testid="case-study-detail-category">
              {caseStudy.category}
            </p>
            <h1 className="mt-2 font-[Playfair_Display] text-4xl text-[#1a1a1a]" data-testid="case-study-detail-title">
              {caseStudy.title}
            </h1>
            <p className="mt-4 font-[Manrope] text-sm leading-relaxed text-[#555555]" data-testid="case-study-detail-summary">
              {caseStudy.summary}
            </p>
            <p className="mt-3 font-[Manrope] text-sm text-[#555555]" data-testid="case-study-detail-treatment">
              <strong>Treatment:</strong> {caseStudy.treatment}
            </p>
            <p className="mt-3 font-[Manrope] text-sm text-[#555555]" data-testid="case-study-detail-duration">
              <strong>Duration:</strong> {caseStudy.duration}
            </p>
            <p className="mt-3 font-[Manrope] text-sm text-[#555555]" data-testid="case-study-detail-outcome">
              <strong>Outcome:</strong> {caseStudy.outcome}
            </p>
          </article>
        </AnimatedSection>

        <AnimatedSection className="mt-8" testId="case-study-detail-back-link-section">
          <Link
            to="/case-studies"
            className="inline-flex rounded-full border border-[#c5a059] px-5 py-2.5 font-[Manrope] text-xs font-semibold uppercase tracking-[0.15em] text-[#b08d45] hover:bg-[#f9f4e8]"
            data-testid="case-study-detail-back-to-case-studies-link"
          >
            Back to Case Studies
          </Link>
        </AnimatedSection>
      </main>
    </PageTransition>
  );
}
